import { Extension } from '@tiptap/core'
import CharacterCount from '@tiptap/extension-character-count'
import Placeholder from '@tiptap/extension-placeholder'
import TextStyle from '@tiptap/extension-text-style'
import StarterKit from '@tiptap/starter-kit'

import { removeClass } from './utils'

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    customExtension: {
      /**
       * Comments will be added to the autocomplete.
       */
      reassignClasses: (props: {
        classes: Array<string>
        removeClassNamePatterns: Array<string>
      }) => ReturnType
      unsetClass: (className: string) => ReturnType
    }
  }
}

export const ClassNameExtension = Extension.create({
  name: 'className',
  addOptions() {
    return {
      types: ['textStyle'],
    }
  },
  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          class: {
            default: null,
            parseHTML: (element) => element.classList.value,
            renderHTML: (attributes) =>
              attributes.class ? { class: attributes.class } : {},
          },
        },
      },
    ]
  },
  addCommands() {
    return {
      reassignClasses:
        ({ classes, removeClassNamePatterns = [] }) =>
        ({ chain, state, tr }) => {
          // Get the current selection state
          const { empty, ranges } = state.selection
          const textStyle = state.schema.marks.textStyle

          // Collect all classes in the current selection
          let currentClass = ranges.reduce((acc, { $from, $to }) => {
            state.doc.nodesBetween($from.pos, $to.pos, (node) => {
              if (node.marks.some((mark) => mark.type === textStyle)) {
                acc += node.marks.map((mark) => mark.attrs.class).join(' ')
              }
            })

            return acc
          }, '')

          // Handle empty selection (cursor position)
          if (empty) {
            currentClass =
              state.storedMarks?.find((mark) => mark.type === textStyle)?.attrs
                .class || ''

            // Remove classes matching the specified patterns
            let finalClass = removeClass(
              currentClass,
              ...removeClassNamePatterns,
            )

            // Add the classes that should be present (avoid duplicates)
            classes.forEach((cls) => {
              if (!finalClass.split(' ').includes(cls)) {
                finalClass += ` ${cls}`
              }
            })

            finalClass = finalClass.trim()

            return chain().setMark(textStyle, { class: finalClass }).run()
          } else {
            // Handle non-empty selection
            ranges.forEach(({ $from, $to }) => {
              state.doc.nodesBetween($from.pos, $to.pos, (node, pos) => {
                const from = Math.max(pos, $from.pos)
                const to = Math.min(pos + node.nodeSize, $to.pos)

                // Get current classes for the current range
                const currentClasses = node.marks
                  .filter((mark) => mark.type === textStyle)
                  .map((mark) => mark.attrs.class)
                  .join(' ')

                // Remove classes matching the specified patterns
                let updatedClass = removeClass(
                  currentClasses,
                  ...removeClassNamePatterns,
                )

                // Add the classes that should be present (avoid duplicates)
                classes.forEach((cls) => {
                  if (!updatedClass.split(' ').includes(cls)) {
                    updatedClass += ` ${cls}`
                  }
                })

                updatedClass = updatedClass.trim()

                // Update the class using the transaction
                tr.addMark(from, to, textStyle.create({ class: updatedClass }))
              })
            })
          }

          return true
        },

      unsetClass:
        (className = '') =>
        ({ chain, state }) => {
          let currentClass =
            state.storedMarks?.find(
              (mark) => mark.type === state.schema.marks.textStyle,
            )?.attrs.class || ''

          if (className) {
            currentClass = removeClass(currentClass, className)
          }

          return chain()
            .setMark(state.schema.marks.textStyle, { class: currentClass })
            .run()
        },
    }
  },
})

const CustomEnterKey = Extension.create({
  name: 'customEnterKey',
  addKeyboardShortcuts() {
    return {
      'Shift-Enter': () => {
        // Custom behavior for Shift + Enter
        this.editor.commands.insertContent('<br />')

        return true // Prevent default behavior
      },
      Enter: () => {
        return true
      },
    }
  },
})

export const getDefaultExtensions = ({
  placeholder,
}: {
  placeholder: string
}) => [
  TextStyle,
  StarterKit.configure({
    heading: false,
    orderedList: false,
    blockquote: false,
    horizontalRule: false,
    strike: false,
  }),
  Placeholder.configure({
    placeholder: placeholder || 'Type something...',
  }),
  CharacterCount.configure({
    limit: 6500,
  }),
  CustomEnterKey,
  ClassNameExtension,
]
